<template>
  <Notifications />

  <SignInView v-if="showSignIn" :checking="!authChecked" />

  <div v-else class="tw-flex tw-min-h-screen tw-flex-col tw-overflow-hidden">
    <Header :version="'1.16.22'" />

    <main
      class="tw-flex-1 tw-transition-[padding] tw-duration-300 tw-ease-in-out"
      :class="[emptyStateClass, headerPinnedClass]"
    >
      <div v-if="showEmptyState" class="tw-text-center">
        <Heading level="h1" class="tw-text-white/68">
          Click on the plus button in the upper menu for selecting the stations.
        </Heading>
      </div>

      <StationGrid v-else class="tw-m-2 lg:tw-m-5" />
    </main>

    <Logo class="tw-fixed tw-bottom-5 tw-right-5 tw-z-[-1] tw-hidden tw-w-28 lg:tw-block" />

    <VpnModal v-model="vpnModalOpen" />
    <InternalLinkWarningModal />
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, watch, watchEffect } from 'vue';

import { Heading, Logo } from '@slideslive/fuse-kit/vue';

import Header from '@/components/Header.vue';
import InternalLinkWarningModal from '@/components/InternalLinkWarningModal.vue';
import Notifications from '@/components/Notifications.vue';
import SignInView from '@/components/SignInView.vue';
import StationGrid from '@/components/StationGrid.vue';
import VpnModal from '@/components/VpnModal.vue';
import useStateNotifications from '@/composables/state_notifications';
import { useStationsFilterParam } from '@/composables/use_stations_filter_param';
import useAuthStore from '@/stores/auth';
import useSettingsStore from '@/stores/settings';
import useStationsStore from '@/stores/stations';

import useVpnStore from './stores/vpn';

const vpnStore = useVpnStore();
const { refresh: refreshVpn, refreshClients: refreshVpnClients } = vpnStore;
const { connected: vpnConnected, mode: connectionMode } = storeToRefs(vpnStore);

const authStore = useAuthStore();
const { isPublic, checked: authChecked, authenticated } = storeToRefs(authStore);

// On the public (HTTPS) deployment, block the app behind sign-in until the proxy
// confirms an alex-member session. Over the VPN (http) isPublic is false — no gate.
const showSignIn = computed(() => isPublic.value && (!authChecked.value || !authenticated.value));

const stationsStore = useStationsStore();
const { selectedStationsList, selectedHosts } = storeToRefs(stationsStore);
const { refreshAll: refreshAllStations } = stationsStore;

useStationsFilterParam(selectedHosts);
useStateNotifications();

const settingsStore = useSettingsStore();
const { headerPinned, regionPreference } = storeToRefs(settingsStore);

// Re-select region and rebuild station URLs when mode/region preference changes.
watch([connectionMode, regionPreference], async () => {
  await refreshVpn({ remeasure: true });
  await refreshAllStations();
});

const vpnModalOpen = ref(false);

watchEffect(() => {
  if (vpnConnected.value !== undefined) {
    vpnModalOpen.value = !vpnConnected.value;
  }
});

let refreshInterval = null;
let clientsRefreshInterval = null;

onMounted(async () => {
  // Resolve region first: in proxy mode the per-station base URL (and the auth
  // endpoint) depend on the selected region.
  await refreshVpn();

  // On the public deployment, verify the session before loading any station data.
  // If not signed in, stop here — the sign-in view is shown and boot resumes after
  // the user returns from OAuth (fresh page load).
  await authStore.check();
  if (isPublic.value && !authenticated.value) return;

  refreshAllStations();

  refreshInterval = setInterval(() => {
    if (document.visibilityState === 'visible') {
      refreshAllStations({ silent: true });
    }
  }, 5000);

  // Keep the WireGuard handshake data live so the `online` flag (and the
  // online-transition auto-refresh watcher) reflect stations going up/down mid-session.
  clientsRefreshInterval = setInterval(() => {
    if (document.visibilityState === 'visible') {
      refreshVpnClients();
    }
  }, 15000);
});

onUnmounted(() => {
  clearInterval(refreshInterval);
  clearInterval(clientsRefreshInterval);
});

const showEmptyState = computed(() => selectedStationsList.value.length === 0);
const emptyStateClass = computed(() =>
  showEmptyState.value ? 'tw-flex tw-flex-1 tw-items-center tw-justify-center' : '',
);
const headerPinnedClass = computed(() => (headerPinned.value ? 'tw-pt-20' : 'tw-pt-0'));
</script>
